/**
 * @file avatar.tsx
 */

"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { cn } from "visuais/fmt";

/**
 * @returns {JSX.Element} Avatar principal.
 * @example
 * ```tsx
 * <Avatar><AvatarImage src="foto.jpg" /><AvatarFallback>JD</AvatarFallback></Avatar>
 * ```
 */
export function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        "relative flex size-8 shrink-0 overflow-hidden rounded-full",
        className
      )}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Imagem do avatar.
 * @example
 * ```tsx
 * <AvatarImage src="foto.jpg" />
 * ```
 */
export function AvatarImage({
  className,
  src,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  // Não renderiza se a src estiver vazia ou nula
  if (!src) {
    return null;
  }

  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full", className)}
      src={src}
      {...props}
    />
  );
}

/**
 * @returns {JSX.Element} Fallback do avatar.
 * @example
 * ```tsx
 * <AvatarFallback>JD</AvatarFallback>
 * ```
 */
export function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted flex size-full items-center justify-center rounded-full",
        className
      )}
      {...props}
    />
  );
}
